import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Table, InputNumber, Input, DatePicker, Select, Switch, Space,
  Spin, Alert, Button, Tag, Tooltip,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../libs/api';
import PageLayout from '../components/layout/PageLayout';

const { Option } = Select;

// Every figure comes from the backend, which runs the same STC / bSTC / PDRS
// modules the scenario model uses — the cheatsheet cannot drift from a real
// scenario's numbers.

const money = (v) => v == null ? '-' : '$' + Math.round(v).toLocaleString();
const money2 = (v) => v == null ? '-' : '$' + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Red at the bottom of a table's range, green at the top. Lightness stays high so
// the value keeps its contrast; zero and n/a cells go grey instead of deep red,
// because "not claimable" is not the same statement as "a poor result".
function heatStyle(value, min, max) {
  if (value == null || value === 0) return { background: '#fafafa', color: '#bbb' };
  if (max <= min) return { background: 'hsl(120, 45%, 90%)' };
  const t = (value - min) / (max - min);
  const hue = Math.round(t * 120);              // 0 red -> 120 green
  return { background: `hsl(${hue}, 62%, ${90 - Math.round(t * 6)}%)` };
}

function SectionTable({ section, mode }) {
  const metric = (c) => (mode === 'per' ? c.per : c.total);

  // Range is per table, so each grid is shaded against its own spread rather
  // than against the largest number on the page
  const values = [];
  for (const r of section.rows) for (const c of r.cells) {
    const v = metric(c);
    if (v != null && v !== 0) values.push(v);
  }
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 0;

  const columns = [
    {
      title: section.rowLabel,
      dataIndex: 'label',
      width: 110,
      fixed: 'left',
      render: (v) => <b>{v}</b>,
    },
    ...section.cols.map((col, i) => ({
      title: (
        <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
          <div>{col.label}</div>
          {col.sub && <div style={{ color: '#999', fontWeight: 400, fontSize: 11 }}>{col.sub}</div>}
        </div>
      ),
      dataIndex: ['cells', i],
      align: 'right',
      width: 108,
      render: (_, row) => {
        const c = row.cells[i];
        const v = metric(c);
        return (
          <Tooltip title={c.note ? `${money(c.total)} total · ${money2(c.per)} ${section.perLabel}${c.note ? ` · ${c.note}` : ''}` : null}>
            <div style={{ ...heatStyle(v, min, max), margin: -8, padding: '8px 8px', fontVariantNumeric: 'tabular-nums' }}>
              {v ? (mode === 'per' ? money2(v) : money(v)) : '—'}
            </div>
          </Tooltip>
        );
      },
    })),
  ];

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space size="small" wrap>
          <span>{section.title}</span>
          {!section.applicable && <Tag color="default">not applicable</Tag>}
          <span style={{ fontWeight: 400, color: '#999', fontSize: 11 }}>
            {mode === 'per' ? section.perLabel : 'total $'}
          </span>
        </Space>
      }
    >
      <div style={{ color: '#888', fontSize: 12, marginBottom: 8 }}>{section.subtitle}</div>
      {!section.applicable && section.notApplicableReason && (
        <Alert type="info" showIcon style={{ marginBottom: 8 }}
          message={<span style={{ fontSize: 12 }}>No values shown — {section.notApplicableReason}.</span>} />
      )}
      <Table
        dataSource={section.rows}
        columns={columns}
        rowKey="key"
        size="small"
        pagination={false}
        bordered
        scroll={{ x: 110 + section.cols.length * 108 }}
      />
    </Card>
  );
}

function RebateCheatsheet() {
  const [postcode, setPostcode] = useState('2000');
  const [installDate, setInstallDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [stcPrice, setStcPrice] = useState(38);
  const [pdrsPrice, setPdrsPrice] = useState(2.8);
  const [network, setNetwork] = useState('ausgrid');
  const [withSolar, setWithSolar] = useState(true);
  const [mode, setMode] = useState('total');       // 'total' | 'per'

  /**
   * One named system, priced beside the grids.
   *
   * Typed values are debounced before they reach the query: the endpoint
   * rebuilds every grid on each call, and a keystroke per request would refetch
   * five tables to change four numbers.
   */
  const [sys, setSys] = useState({ pvKw: null, batteryKwh: null, inverterKw: null });
  const [sysQuery, setSysQuery] = useState({ pvKw: null, batteryKwh: null, inverterKw: null });
  useEffect(() => {
    const t = setTimeout(() => setSysQuery(sys), 350);
    return () => clearTimeout(t);
  }, [sys]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/api/rebate-cheatsheet', {
        params: {
          postcode, installDate, stcPrice, pdrsPrice, network, withSolar: withSolar ? 1 : 0,
          // Omitted rather than sent as zero, so the server can tell "nothing
          // entered" from "entered as nothing" and leave the quote off entirely.
          ...(sysQuery.pvKw ? { pvKw: sysQuery.pvKw } : {}),
          ...(sysQuery.batteryKwh ? { batteryKwh: sysQuery.batteryKwh } : {}),
          ...(sysQuery.inverterKw ? { inverterKw: sysQuery.inverterKw } : {}),
        },
      });
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load the rebate cheatsheet');
    }
    setLoading(false);
  }, [postcode, installDate, stcPrice, pdrsPrice, network, withSolar,
    sysQuery.pvKw, sysQuery.batteryKwh, sysQuery.inverterKw]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const a = data?.assumptions;
  const quote = data?.quote;

  return (
    <PageLayout>
      <div className="page-header">
        <h1>Rebate Cheatsheet</h1>
        <p>Quick reference for STC, bSTC and NSW PDRS — and what the two schemes are worth stacked</p>
      </div>


      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      <Card title="Assumptions" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 12]}>
          <Col xs={12} md={4}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Install date</div>
            <DatePicker
              value={installDate ? dayjs(installDate) : null}
              onChange={(d) => setInstallDate(d ? d.format('YYYY-MM-DD') : null)}
              format="DD/MM/YYYY" allowClear={false} size="small" style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} md={4}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Postcode</div>
            <Input value={postcode} onChange={(e) => setPostcode(e.target.value)} maxLength={4} size="small" />
            <div style={{ fontSize: 11, color: '#999', marginTop: 2, minHeight: 16 }}>
              {a && (a.isNsw
                ? <span style={{ color: '#05A677' }}>NSW — PDRS applies</span>
                : <span>not NSW — PDRS does not apply</span>)}
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>STC price ($/STC)</div>
            <InputNumber value={stcPrice} onChange={setStcPrice} min={0} step={0.01} prefix="$" size="small" style={{ width: '100%' }} />
          </Col>
          {a?.isNsw && (
            <>
              <Col xs={12} md={4}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>PDRS price ($/PRC)</div>
                <InputNumber value={pdrsPrice} onChange={setPdrsPrice} min={0} step={0.01} prefix="$" size="small" style={{ width: '100%' }} />
              </Col>
              <Col xs={12} md={4}>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>NSW network</div>
                <Select value={network} onChange={setNetwork} size="small" style={{ width: '100%' }}>
                  <Option value="ausgrid">Ausgrid (1.04)</Option>
                  <Option value="endeavour">Endeavour (1.05)</Option>
                  <Option value="essential">Essential (1.05)</Option>
                </Select>
              </Col>
            </>
          )}
          <Col xs={24} md={4}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Show</div>
            <Space size="small">
              <Switch size="small" checked={mode === 'per'} onChange={(c) => setMode(c ? 'per' : 'total')} />
              <span style={{ fontSize: 12 }}>{mode === 'per' ? 'per kW / kWh' : 'total $'}</span>
            </Space>
          </Col>
          {a?.isNsw && (
            <Col xs={24} md={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>New solar with the battery</div>
              <Space size="small">
                <Switch size="small" checked={withSolar} onChange={setWithSolar} />
                <span style={{ fontSize: 12 }}>
                  {withSolar ? 'yes — PDRS rate 0.1' : 'no — PDRS rate 0.067'}
                </span>
              </Space>
              <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                The higher rate needs new PV of at least a quarter of the usable battery capacity.
              </div>
            </Col>
          )}
          <Col xs={24} md={6}>
            <Button icon={<ReloadOutlined />} onClick={fetchData} size="small" style={{ marginTop: 22 }}>Refresh</Button>
          </Col>
        </Row>
        {/* ------------------------------------------------------------------
            This system. Its own inset because it drives nothing else on the
            page: the grids above and below are unchanged by whatever is typed
            here.
            ------------------------------------------------------------------ */}
        <div
          style={{
            marginTop: 14,
            padding: '12px 14px',
            background: '#fafcfd',
            border: '1px solid #e8eef2',
            borderRadius: 6,
          }}
        >
          <Row gutter={[16, 12]} align="top">
            <Col xs={24} md={7}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>This system</div>
              <Space size="small" wrap>
                <div>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>PV (kW)</div>
                  <InputNumber
                    value={sys.pvKw} onChange={(v) => setSys((x) => ({ ...x, pvKw: v }))}
                    min={0} step={0.1} size="small" style={{ width: 88 }} placeholder="—"
                  />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Battery (kWh)</div>
                  <InputNumber
                    value={sys.batteryKwh} onChange={(v) => setSys((x) => ({ ...x, batteryKwh: v }))}
                    min={0} step={1} size="small" style={{ width: 96 }} placeholder="—"
                  />
                </div>
                <Tooltip title="PDRS needs it: counted capacity is capped at 4 x inverter output, and without it no certificates can be worked out.">
                  <div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Inverter (kW)</div>
                    <InputNumber
                      value={sys.inverterKw} onChange={(v) => setSys((x) => ({ ...x, inverterKw: v }))}
                      min={0} step={0.1} size="small" style={{ width: 92 }} placeholder="—"
                    />
                  </div>
                </Tooltip>
              </Space>
            </Col>

            <Col xs={24} md={17}>
              {!quote ? (
                <div style={{ fontSize: 12, color: '#999', paddingTop: 26 }}>
                  Enter a PV or battery size to price this system against every scheme at once.
                </div>
              ) : (
                <>
                  <Row gutter={[10, 10]}>
                    {[
                      {
                        k: 'stc',
                        label: 'Solar STC',
                        n: quote.stc.certificates,
                        v: quote.stc.value,
                        unit: 'STCs',
                        reason: quote.stc.reason,
                      },
                      {
                        k: 'bstc',
                        label: 'Battery bSTC',
                        n: quote.bstc.certificates,
                        v: quote.bstc.value,
                        unit: 'STCs',
                        reason: quote.bstc.reason,
                      },
                      {
                        k: 'bess4',
                        label: 'PDRS BESS4',
                        n: quote.pdrs.bess4.certificates,
                        v: quote.pdrs.bess4.value,
                        unit: 'PRCs',
                        reason: quote.pdrs.bess4.reason,
                      },
                      {
                        k: 'bess5',
                        label: 'PDRS BESS5',
                        n: quote.pdrs.bess5.certificates,
                        v: quote.pdrs.bess5.value,
                        unit: 'PRCs',
                        reason: quote.pdrs.bess5.reason,
                      },
                      {
                        k: 'total',
                        label: 'Stacked total',
                        n: null,
                        v: quote.totalValue,
                        unit: null,
                        reason: null,
                        strong: true,
                      },
                    ].map((it) => (
                      <Col xs={12} sm={8} md={it.k === 'total' ? 24 : 5} key={it.k}>
                        <Tooltip title={it.reason || null}>
                          <div
                            style={{
                              border: '1px solid',
                              borderColor: it.strong ? '#54A2CC' : '#e8eef2',
                              background: it.strong ? '#f0f8fc' : '#fff',
                              borderRadius: 5,
                              padding: '6px 10px',
                              minHeight: 58,
                            }}
                          >
                            <div style={{ fontSize: 11, color: '#888' }}>{it.label}</div>
                            <div
                              style={{
                                fontSize: it.strong ? 18 : 16,
                                fontWeight: 600,
                                fontVariantNumeric: 'tabular-nums',
                                color: it.v ? (it.strong ? '#2b6a88' : '#222') : '#bbb',
                              }}
                            >
                              {it.v ? money(it.v) : '—'}
                            </div>
                            {it.unit && (
                              <div style={{ fontSize: 11, color: it.n ? '#666' : '#bbb' }}>
                                {it.n ? `${it.n.toLocaleString()} ${it.unit}` : (it.reason ? 'not claimable' : `0 ${it.unit}`)}
                              </div>
                            )}
                          </div>
                        </Tooltip>
                      </Col>
                    ))}
                  </Row>

                  {/* The conditions that decide whether the figures above stand */}
                  <div style={{ marginTop: 8, fontSize: 11, color: '#888', lineHeight: 1.7 }}>
                    {quote.pdrs.activity && (
                      <Tag color="blue" style={{ marginRight: 6 }}>
                        {quote.pdrs.activity} · counted {quote.pdrs.cappedKwh} kWh
                      </Tag>
                    )}
                    {quote.pdrs.applies && quote.pdrs.activity && !quote.pdrs.solarMeetsMinimum && (
                      <Tag color="orange" style={{ marginRight: 6 }}>
                        lower PDRS rate — needs {quote.pdrs.minSolarKwForHigherRate} kW of new PV
                      </Tag>
                    )}
                    {quote.pdrs.equipmentRuleBreached && (
                      <Tag color="red" style={{ marginRight: 6 }}>
                        breaches 6 x inverter rule (limit {quote.pdrs.equipmentRuleLimitKwh} kWh)
                      </Tag>
                    )}
                    {quote.bstc.period && quote.bstc.certificates > 0 && (
                      <span>bSTC {quote.bstc.period} x{quote.bstc.factor} · </span>
                    )}
                    {!quote.pdrs.applies && <span>PDRS is NSW only · </span>}
                    at ${a?.stcPrice}/STC{a?.isNsw ? ` and $${a.pdrsPrice}/PRC` : ''}
                  </div>
                </>
              )}
            </Col>
          </Row>
        </div>

        {a && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#888' }}>
            Solar zone rating <b>{a.zoneRating ?? 'n/a'}</b> ·
            bSTC period <b>{a.bstcPeriod}</b> (factor x{a.bstcFactor}) ·
            STC <b>${a.stcPrice}</b>
            {a.isNsw && <> · PDRS <b>${a.pdrsPrice}</b> · {a.networkLabel} loss factor <b>{a.networkLossFactor}</b></>}
          </div>
        )}
      </Card>

      <Spin spinning={loading}>
        {(data?.sections || []).map((s) => (
          <SectionTable key={s.key} section={s} mode={mode} />
        ))}
      </Spin>
    </PageLayout>
  );
}

export default RebateCheatsheet;
